import React from 'react';

// Rider-side statuses that mean the rider has reached the door. Mirrors
// _RIDER_VISIT_HAPPENED_STATUSES in dispatch.py, which picks the close lever: a visit that
// happened is force-completed, one that never started is cancelled. Either way the rider
// is told "closed by the office" and carries on.
export const VISIT_HAPPENED_STATUSES = new Set([
  'ARRIVED',
  'TRIAL_IN_PROGRESS',
  'PAYMENT_PENDING',
  'PAYMENT_CONFIRMED',
  'ENTRY_DECLINE_PENDING',
  'RETURNING_TO_WAREHOUSE',
  'RECONCILIATION_PENDING',
]);

// `outcome` is what the rider backend records for the stop: nothing for a sale made some
// other way (the sale is read from the Orders sheet), RTO/DOL when nothing was sold. The
// nothing-sold reason picks RTO or DOL by whether the rider had reached the door.
export const CLOSE_REASONS = [
  {
    key: 'paid_outside',
    label: 'Customer paid outside the rider app',
    detail: 'The sale is on the Orders sheet. Sold pieces come off the pack at reversal.',
    text: 'Paid outside the rider app',
  },
  {
    key: 'old_app',
    label: 'Order logged with the old app',
    detail: 'Same: the sale is already on the Orders sheet.',
    text: 'Logged with the old app',
  },
  {
    key: 'nothing_sold',
    label: 'Nothing was sold',
    detail: 'The visit did not end in a sale. Recorded as a return, or as a visit that did not happen if the rider had not reached the door.',
    text: 'Nothing sold',
    returns: true,
  },
];

function Chip({ tone = 'gray', children, title }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-blue-50 text-blue-700',
    pink: 'bg-pink-50 text-zoddle-pink',
    amber: 'bg-amber-50 text-amber-700 border border-amber-200',
  };
  return (
    <span title={title} className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${tones[tone]}`}>
      {children}
    </span>
  );
}

const statusText = status => (status || '').replace(/_/g, ' ');

// Visits of one rider, split into their trips (a trip is the stops sharing a first stop id)
// and single visits. Stops of a trip are in visiting order, finished ones included.
function groupVisits(riderVisits) {
  const groups = [];
  const trips = new Map();
  riderVisits.forEach(visit => {
    if (!visit.trip_job_ids) {
      groups.push({ trip: false, stops: [visit] });
      return;
    }
    const key = visit.trip_job_ids[0];
    if (!trips.has(key)) {
      const group = { trip: true, stops: [] };
      trips.set(key, group);
      groups.push(group);
    }
    trips.get(key).stops.push(visit);
  });
  groups.forEach(group => {
    if (group.trip) group.stops.sort((a, b) => (a.stop_sequence || 0) - (b.stop_sequence || 0));
  });
  return groups;
}

function StopRow({ visit, onClose, busyId }) {
  const closed = visit.finished && visit.closed_by_office;
  const done = visit.finished && !visit.closed_by_office;
  return (
    <div
      className={`grid grid-cols-[44px_1.25fr_1.35fr_1fr_128px] items-center gap-3 px-4 py-3 border-t border-gray-100 text-sm ${
        closed ? 'text-gray-400' : ''
      }`}
    >
      <span>
        {visit.stop_sequence ? (
          <span
            className={`flex h-[26px] w-[26px] items-center justify-center rounded-full text-xs font-bold ${
              closed || done
                ? 'border-[1.5px] border-dashed border-gray-300 bg-white text-gray-400'
                : 'bg-gray-800 text-white'
            }`}
          >
            {visit.stop_sequence}
          </span>
        ) : null}
      </span>
      <div>
        <div className={`font-semibold ${closed ? 'line-through text-gray-500' : 'text-gray-800'}`}>
          {visit.customer_name || '-'}
        </div>
        <div className="text-xs font-mono text-gray-400">
          {visit.source_job_id}
          {visit.customer_area ? ` · ${visit.customer_area}` : ''}
        </div>
      </div>
      <div className="flex flex-col items-start gap-1">
        {closed ? (
          <Chip>CLOSED · bag still out</Chip>
        ) : done ? (
          <Chip tone="gray">DONE</Chip>
        ) : (
          <Chip tone="blue">{statusText(visit.status)}</Chip>
        )}
        {visit.paid_outside_rider_app && !done && (
          <Chip
            tone="pink"
            title="A completed order for this appointment came from outside the rider app, so the rider-app flow will never settle on its own."
          >
            Paid outside rider app
          </Chip>
        )}
        {visit.zocs_closed && !visit.finished && (
          <span className="text-xs text-amber-700">ZOCS: {visit.operational_status}</span>
        )}
      </div>
      <div className="text-gray-700">
        {visit.finished ? (
          <span className="text-xs text-gray-500">
            {closed ? `${visit.outstanding_items ?? 0} coming back` : ''}
          </span>
        ) : (
          <>
            {visit.outstanding_items ?? 0} item{(visit.outstanding_items ?? 0) === 1 ? '' : 's'}
            {visit.unpaid_with_customer > 0 && (
              <div className="text-xs text-zoddle-pink">{visit.unpaid_with_customer} unpaid</div>
            )}
          </>
        )}
      </div>
      <div className="text-right">
        {visit.finished ? (
          <span className="text-xs text-gray-500">{closed ? 'Reverse when back' : ''}</span>
        ) : (
          <button
            onClick={() => onClose(visit)}
            disabled={busyId === visit.source_job_id}
            className="text-sm font-semibold border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            {busyId === visit.source_job_id ? 'Closing...' : 'Close stop…'}
          </button>
        )}
      </div>
    </div>
  );
}

// Every job the rider app holds open, one card per trip (or per rider's single visits).
// A stop the office already closed stays on its trip, struck through, until the trip ends,
// because its bag is still with the rider and still has to come back.
export default function LiveJobsBoard({ visits, onClose, busyId }) {
  if (!visits.length) {
    return (
      <div className="text-gray-500 italic py-8 text-center bg-white border border-gray-200 rounded">
        No rider is out on a job right now.
      </div>
    );
  }
  const byRider = new Map();
  visits.forEach(visit => {
    const key = visit.rider_id || visit.rider_name || 'Unassigned';
    if (!byRider.has(key)) byRider.set(key, []);
    byRider.get(key).push(visit);
  });

  return (
    <div className="space-y-4">
      {[...byRider.values()].flatMap(riderVisits =>
        groupVisits(riderVisits).map(group => {
          const [first] = group.stops;
          const open = group.stops.filter(v => !v.finished);
          const closedStops = group.stops.filter(v => v.finished && v.closed_by_office).length;
          const stuck = open.filter(v => v.zocs_closed).length;
          const minutes = Math.max(...open.map(v => v.minutes_open ?? 0), 0);
          return (
            <div
              key={`${first.rider_id}-${first.source_job_id}`}
              className="bg-white border border-gray-200 rounded overflow-hidden"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-2 border-b border-gray-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  {first.rider_name || 'Unknown rider'}
                  {group.trip && <Chip tone="blue">Trip · {group.stops.length} stops</Chip>}
                  <span className="text-xs font-normal text-gray-500">
                    {minutes ? `out ${minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`}` : ''}
                  </span>
                </div>
                <div className="flex gap-2">
                  {closedStops > 0 && <Chip tone="amber">{closedStops} closed · bag still out</Chip>}
                  {stuck > 0 && <Chip tone="amber">{stuck} closed in ZOCS</Chip>}
                </div>
              </div>
              {group.stops.map(visit => (
                <StopRow key={visit.source_job_id} visit={visit} onClose={onClose} busyId={busyId} />
              ))}
            </div>
          );
        })
      )}
    </div>
  );
}
